import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import 'ag-grid-enterprise'

import { BooleanFormatter, BooleanRenderer } from '@hr/common/gridcomponents.jsx'
import { deleteEducation } from '../../../actions/competences/educations/educationdelete'
import { getEducation } from '../../../actions/competences/educations/educationget'
import { postEducation } from '../../../actions/competences/educations/educationpost'
import { getAllEducationsForPerson } from '../../../actions/competences/educations/educationsgetallforperson'
//import { getAllEducationsForPersonModified } from '../../../actions/competences/educations/educationsgetallforpersonmodified'
import { getAllEducationsForPersons } from '../../../actions/competences/educations/educationsgetallforpersons'
//import { getAllEducationsModified } from '../../../actions/competences/educations/educationsgetallmodified'
import { addGridData, getLocaleText, removeGridData, resetSorting, updateGridData } from '../../../actions/datagrid'
import { Button } from '../../../common/Button.tsx'
import {
	contextMenu,
	defaultColDef,
	gridSettings,
	loadViewState,
	restoreViewState,
	saveViewState,
	sideBar,
	statusBar,
} from '../../../common/grid'
import {
	IconAddNew,
	pencilIconString,
	restoreIconString,
	TemplateProfileImage,
	trashIconString,
} from '../../../common/icons'
import { PdfReport } from '../../../common/pdfreport'
import { fetchSettings } from '../../../common/settings.js'
import { dateFormatter, dateGetter, tagFormatter /*useInterval*/ } from '../../../common/utils'
import {
	actionTypeSelector,
	authSelector,
	educationSelector,
	/*educationsModifiedSelector,*/
	educationsSelector,
} from '../../../reducers'
import { AddEducation } from './educationmodal'

const viewStateKey = 'educationViewState'

export default function EducationView({ person }) {
	const { t } = useTranslation()
	const dispatch = useDispatch()

	const path = window.location.pathname
	const isPersonal = path.startsWith('/personal')

	const actionType = useSelector(actionTypeSelector)
	const { admin, companyName } = useSelector(authSelector)
	const educations = useSelector(educationsSelector)
	const education = useSelector(educationSelector)

	const [settings, setSettings] = useState({})

	const [report, setReport] = useState(null)
	const closeReport = () => setReport(null)

	const [modalShow, setModalShow] = useState(false)
	const [showLoading, setShowLoading] = useState(false)

	const gridRef = useRef()

	useEffect(() => {
		if (!person && !isPersonal) {
			dispatch(getAllEducationsForPersons(true))
		}
		return () => dispatch({ type: 'EDUCATIONS_UNMOUNT', payload: { educations: null } })
	}, [person, isPersonal, dispatch])

	useEffect(() => {
		if (person) {
			if (person?.personId) {
				dispatch(getAllEducationsForPerson(person?.personId))
			}
		}
	}, [person, dispatch])
	/*
	const fetchModifiedEducations = useCallback((modifiedSince) => dispatch(getAllEducationsModified(modifiedSince)))
	const fetchModifiedEducationsForPerson = useCallback((personId, modifiedSince) =>
		dispatch(getAllEducationsForPersonModified(person?.personId, modifiedSince)),
	)
	const [modifiedSince, setModifiedSince] = useState(Date.now())
	// Poll modified educations every 30 seconds
	if (import.meta.env.TEST) {
		// production
		useInterval(() => {
			const tmpModifiedSince = modifiedSince
			setModifiedSince(Date.now())
			if (person?.personId) {
				fetchModifiedEducationsForPerson(person?.personId, tmpModifiedSince)
			} else {
				fetchModifiedEducations(tmpModifiedSince)
			}
		}, 30 * 1000)
	}
	const educationsModified = useSelector(educationsModifiedSelector)
	useEffect(() => {
		if (gridRef.current.api) {
			if (educationsModified?.newEducations?.length > 0) {
				addGridData(gridRef, educationsModified.newEducations, 0)
			}
			if (educationsModified?.modifiedEducations?.length > 0) {
				updateGridData(gridRef, educationsModified.modifiedEducations)
			}
			if (educationsModified?.deletedEducations?.length > 0) {
				removeGridData(gridRef, educationsModified.deletedEducations)
			}
		}
	}, [educationsModified])
*/
	useEffect(() => {
		;(async () => {
			setSettings(await fetchSettings())
		})()
	}, [])

	useEffect(() => {
		if (actionType.endsWith('_LOADING')) {
			setShowLoading(true)
		} else if (actionType.endsWith('_SUCCEEDED') || actionType.endsWith('_FAIL') || actionType.endsWith('_DONE')) {
			setShowLoading(false)
		}
		if (actionType === 'POST_EDUCATION_SUCCEEDED') {
			resetSorting(gridRef)
			addGridData(gridRef, [education], 0)
		}
		if (actionType === 'POST_EDUCATION_EDIT_SUCCEEDED') {
			updateGridData(gridRef, [education])
		}
		if (actionType === 'UPLOAD_DONE') {
			person?.personId
				? dispatch(getAllEducationsForPerson(person?.personId))
				: dispatch(getAllEducationsForPersons(true))
			setModalShow(false)
		}
		if (actionType === 'GET_EDUCATION_SUCCEEDED') {
			setModalShow(true)
		}
		if (actionType === 'DELETE_EDUCATION_SUCCEEDED') {
			removeGridData(gridRef, [education])
		}
	}, [actionType, education, person, dispatch])

	const saveData = (data) => dispatch(postEducation(data))
	const removeEducation = useCallback(
		({ id, data: { personName } }) => {
			if (confirm(`${t('DeleteEducationConfirm', 'Haluatko varmasti poistaa tutkinnon?')} (${personName})`)) {
				dispatch(deleteEducation(id))
			}
		},
		[t, dispatch],
	)

	const gridComponents = {
		templateProfileImage: TemplateProfileImage,
	}

	const columnDefs = useMemo(
		() => [
			{
				headerName: t('Number', 'Numero'),
				field: 'personNumber',
				filter: 'agNumberColumnFilter',
				suppressSizeToFit: true,
				minWidth: 115,
				maxWidth: 115,
				width: 115,
			},
			{
				headerName: t('Name', 'Nimi'),
				field: 'personName',
				width: 150,
			},
			{
				headerName: t('EducationLevel', 'Koulutustaso'),
				field: 'educationLevelCodeItem.codeValue',
				width: 250,
			},
			{
				headerName: t('Degree', 'Tutkinto'),
				field: 'degreeCodeItem.codeValue',
				tooltipField: 'degreeCodeItem.codeValue',
				width: 250,
			},
			{
				headerName: t('FieldOfOrientation', 'Suuntautumisala/pääaine'),
				field: 'fieldOfOrientation',
				width: 250,
			},
			{
				headerName: t('Academy', 'Oppilaitos'),
				field: 'school',
				width: 250,
			},
			{
				headerName: t('DateOfExamination', 'Valmistumispäivä'),
				field: 'graduationDate',
				cellClass: 'dateType',
				filter: 'agDateColumnFilter',
				width: 175,
				valueGetter: dateGetter,
				valueFormatter: dateFormatter,
			},
			{
				headerName: t('GraduationYear', 'Valmistumisvuosi'),
				field: 'graduationYear',
				filter: 'agNumberColumnFilter',
				width: 175,
			},
			{
				headerName: t('EducationNotFinished', 'Opiskelu kesken'),
				field: 'finished',
				valueGetter: (params) => !params.data?.finished,
				cellRenderer: BooleanRenderer,
				filter: 'agSetColumnFilter',
				filterParams: {
					valueFormatter: BooleanFormatter,
				},
			},
			{
				field: 'educationDescription',
				headerName: t('Description', 'Kuvaus'),
			},
			{
				field: 'supervisor',
				headerName: t('Supervisor', 'Esihenkilö'),
			},
			{
				field: 'supervisor2',
				headerName: `${t('Supervisor', 'Esihenkilö')} 2`,
			},
			{
				field: 'dim1',
				headerName: settings.dim1name || t('Dimension1', 'Kustannuspaikka'), // costUnitName
			},
			{
				field: 'dim2',
				headerName: settings.dim2name || t('Dimension2', 'Projekti'), // projectName
			},
			{
				field: 'dim3',
				headerName: settings.dim3name || t('Dimension3', 'Dimensio 3'),
			},
			{
				field: 'dim4',
				headerName: settings.dim4name || t('Dimension4', 'Dimensio 4'),
			},
			{
				field: 'dim5',
				headerName: settings.dim5name || t('Dimension5', 'Dimensio 5'),
			},
			{
				field: 'dim6',
				headerName: t('BusinessId', 'Y-tunnus'),
			},
			{
				headerName: t('Keywords', 'Avainsanat'),
				field: 'tags',
				valueGetter: (params) => tagFormatter(params.data?.tags),
				tooltipValueGetter: (params) => tagFormatter(params.data?.tags),
			},
		],
		[t, settings],
	)

	const initialState = useMemo(
		() => ({
			columnVisibility: {
				...(isPersonal || person
					? { hiddenColIds: ['personNumber', 'personName', 'tags'] }
					: { hiddenColIds: ['supervisor', 'supervisor2'] }),
			},
		}),
		[isPersonal, person],
	)

	const getContextMenuItems = useCallback(
		(params) => {
			let items = []

			if (!isPersonal) {
				items.push({
					name: t('DefaultGridValues', 'Palauta taulukon oletusasetukset'),
					action: () => {
						restoreViewState(viewStateKey, initialState)
					},
					icon: restoreIconString,
				})

				items.push('separator')
			}

			if (!params.api.isPivotMode()) {
				items.push({
					name: t('Edit', 'Muokkaa'),
					icon: pencilIconString,
					action: () => dispatch(getEducation(params.node.id)),
				})

				if (admin) {
					items.push({
						name: t('Delete', 'Poista'),
						icon: trashIconString,
						action: () => removeEducation(params.node),
					})
				}
				items.push('separator')
			}
			items = items.concat(
				contextMenu(t, params, setReport, {
					title: t('Educations', 'Tutkinnot'),
					companyname: companyName,
				}),
			)
			return items
		},
		[companyName, dispatch, isPersonal, admin, t, removeEducation, initialState],
	)

	const doubleClickAction = (row) => !Number.isNaN(Number(row.node.id)) && dispatch(getEducation(row.node.id))

	return (
		<>
			{report !== null && <PdfReport json={report} onCloseReport={closeReport} />}

			{modalShow && <AddEducation show setShow={setModalShow} educationData={education} saveData={saveData} />}
			{!location.pathname.startsWith('/mgmt/persons') && (
				<Row>
					<Col sm="4"></Col>
					<Col sm="8" align="right">
						<Row className="g-2">
							<Col />
							<Col md="auto">
								<Button
									className="competencebtn addbutton"
									onClick={() => {
										// empty fields when creating new
										dispatch({
											type: 'EDUCATION_UNMOUNT',
											payload: {
												education: {
													educationId: 0,
													finished: true,
													personId: person?.personId,
													personNumber: person?.nr,
													personName: person?.name,
												},
											},
										})
										setModalShow(true)
									}}
								>
									<IconAddNew /> {t('AddEducation', 'Lisää tutkinto')}
								</Button>
							</Col>
						</Row>
					</Col>
				</Row>
			)}
			<Row className="ag-theme-material styles-ag flex-grow-1">
				<AgGridReact
					{...gridSettings}
					loading={showLoading}
					ref={gridRef}
					components={gridComponents}
					columnDefs={columnDefs}
					defaultColDef={defaultColDef}
					sideBar={sideBar}
					rowData={educations}
					getContextMenuItems={getContextMenuItems}
					statusBar={statusBar}
					onRowDoubleClicked={doubleClickAction}
					getRowId={(params) => String(params.data.educationId)}
					getLocaleText={(params) => getLocaleText(t, params)}
					animateRows
					suppressCellFocus
					enableCellTextSelection
					initialState={isPersonal || person ? initialState : loadViewState(viewStateKey)}
					onStateUpdated={({ state }) => !isPersonal && saveViewState(state, viewStateKey)}
					debug={import.meta.env.DEV}
				/>
			</Row>
		</>
	)
}
